import { SwiperSlider } from "components/SwiperSlider";
import type { Swiper as SwiperType } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Keyboard } from "swiper/modules";
import { Modal } from "react-bootstrap";
import { useState } from "react";
import { MdOutlineClose } from "react-icons/md";
import { IPrimaryphoto } from "types/artifacts/common";
import { trackEvent } from "services/api-client/matomoTracker";
import { PLACEHOLDER_IMAGE } from "utils/constants";
import { isEmpty } from "utils/helpers";

interface IPhotosSliderWithImagePreviewProps {
    harid: string;
    photos: IPrimaryphoto[];
}

export const PhotosSliderWithImagePreview = ({ harid, photos }: IPhotosSliderWithImagePreviewProps) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [currentIdx, setCurrentIdx] = useState(0);
    const [activeIdx, setActiveIdx] = useState(0);

    const handleThumbClick = (index: number) => {
        trackEvent(`AI Mode / Property Detail Photo Gallery / ${harid}`);
        setCurrentIdx(index);
        setActiveIdx(index);
        setModalOpen(true);
    };

    const closeModal = () => setModalOpen(false);

    return (
        <div className="photo_slider">
            <SwiperSlider sliderStyle={{ height: "166px" }}>
                {photos.map((photo, index) => (
                    <SwiperSlide
                        key={index}
                        className={photos.length < 4 ? "swiper-slider-item photosonly" : "photosonly"}
                    >
                        <div
                            className="cardv2 mb-0"
                            style={{
                                height: "166px",
                                width: "100%",
                                cursor: "pointer",
                                border: "none",
                            }}
                            onClick={() => handleThumbClick(index)}
                        >
                            <img
                                src={!isEmpty(photo.url) ? photo.url : PLACEHOLDER_IMAGE}
                                style={{ borderRadius: "1rem", objectFit: "cover" }}
                                alt={`Property thumbnail ${index + 1}`}
                                onError={(e) => {
                                    e.currentTarget.onerror = null; // Prevent infinite loop if placeholder also fails
                                    e.currentTarget.src = PLACEHOLDER_IMAGE;
                                }}
                            />
                        </div>
                    </SwiperSlide>
                ))}
            </SwiperSlider>

            <Modal
                show={modalOpen}
                onHide={closeModal}
                dialogClassName="modal-fullscreen"
                backdrop="static"
                keyboard={true}
            >
                <button
                    onClick={closeModal}
                    style={{
                        all: "unset",
                        cursor: "pointer",
                        position: "absolute",
                        top: 10,
                        right: 10,
                        zIndex: 1050,
                    }}
                    aria-label="Close"
                >
                    <MdOutlineClose size={30} />
                </button>

                {/* body stretches entire viewport – Swiper takes over */}
                <Modal.Body className="p-0 s1 imagefull_preview position-relative">
                    <Swiper
                        modules={[Navigation, Keyboard]}
                        navigation
                        keyboard={{ enabled: true }}
                        slidesPerView={1}
                        initialSlide={currentIdx}
                        onSlideChange={(swiper: SwiperType) => setActiveIdx(swiper.activeIndex)}
                        style={{ width: "100%", height: "calc(100vh - 30px)" }}
                        onSwiper={(swiper: SwiperType) => {
                            const handler = (e: MouseEvent) => e.preventDefault();
                            const elements = [swiper.navigation?.nextEl, swiper.navigation?.prevEl];
                            elements.forEach((element) => element?.addEventListener("mousedown", handler));
                            swiper.on("destroy", () => {
                                elements.forEach((element) => element?.removeEventListener("mousedown", handler));
                            });
                        }}
                    >
                        {photos.map((photo, i) => (
                            <SwiperSlide key={i}>
                                <div className="d-flex position-relative justify-content-center align-items-center" style={{ height: "100%" }}>
                                    <img
                                        src={!isEmpty(photo.url) ? photo.url : PLACEHOLDER_IMAGE}
                                        alt={photo.caption || `Property photo ${i + 1}`}
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "contain",
                                        }}
                                        onError={(e) => {
                                            e.currentTarget.onerror = null; // Prevent infinite loop if placeholder also fails
                                            e.currentTarget.src = PLACEHOLDER_IMAGE;
                                        }}
                                    />
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                    <div
                        className="position-absolute"
                        style={{
                            bottom: '50px',
                            left: "50%",
                            transform: "translateX(-50%)",
                            color: "#fff",
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            padding: "5px 10px",
                            borderRadius: "5px",
                            zIndex: 1,
                        }}
                    >
                        {photos[activeIdx]?.caption || `Photo ${activeIdx + 1} of ${photos.length}`}
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    )
}
